import { useState, useEffect } from 'react';
import { Button, Spinner, TextControl, ToggleControl, FormTokenField, SelectControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import Header from '../../components/layout/Header';
import Body from '../../components/layout/Body';
import Tabs from '../../components/layout/Tabs';
import { useNotice } from '../../contexts/NoticeContext';
import { useURLParams } from '../../contexts/ParamsContext';
import { fetchTaxonomy, saveTaxonomy } from '../../utils/api';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

// Utility function to sanitize slugs and allow / and {term}/{date}
const generateMetaKey = (label) => {
    return label
        .toLowerCase()
        // allow letters, numbers, underscores, dash, slash, {} 
        .replace(/[^a-z0-9_\/\{\}]+/g, '')
        // remove only leading slashes, allow trailing slash
        .replace(/^\/+/, '');
};

// Validate only allowed placeholders
const allowedPlaceholders = ['{term}', '{date}', '{post}'];
// Validate any placeholders in a slug
const validatePlaceholders = (slug) => {
    // Match all {placeholder} in the string
    const matches = slug.match(/\{[a-zA-Z0-9_-]+\}/g) || [];
    // Every matched placeholder must be in the allowed list
    return matches.every(ph => allowedPlaceholders.includes(ph.toLowerCase()));
};

const TaxonomyEdit = () => {
    const [isSaving, setIsSaving] = useState(false);
    const { params, updateUrlParams } = useURLParams();

    const [rewriteSlugType, setRewriteSlugType] = useState('default');
    const [customRewriteSlug, setCustomRewriteSlug] = useState('');

    const [taxonomyData, setTaxonomyData] = useState({
        id: null,
        name: '',
        singular_name: '',
        slug: '',
        post_types: [],
        public: true,
        hierarchical: false,
        labels: { menu_name: '' },
        visibility: {
            show_in_ui: true,
            show_in_menu: true,
            menu_position: 25,
            menu_icon: 'dashicons-admin-post',
            show_in_rest: true,
        },
        rewrite_slug: '',
    });

    const { showNotice } = useNotice();

    const postTypes = useSelect(
        (select) => select(coreStore).getPostTypes({ per_page: -1 }),
        []
    );

    const filteredPostTypes = (postTypes || []).filter(
        (pt) => pt.viewable && pt.slug !== 'attachment' && pt.slug !== 'voxy-components'
    );

    const postTypeSuggestions = filteredPostTypes.map((pt) => pt.slug);

    useEffect(() => {
        if (!params.id) return;

        fetchTaxonomy(params.id)
            .then(data => {
                setTaxonomyData({
                    id: data.id || null,
                    name: data.name || '',
                    singular_name: data.singular_name || '',
                    slug: data.slug || '',
                    post_types: Array.isArray(data.post_types) ? data.post_types : [],
                    public: data.public === '1',
                    hierarchical: data.hierarchical === '1',
                    labels: {
                        menu_name: data.menu_name || '',
                    },
                    visibility: {
                        show_in_ui: data.show_in_ui === '1',
                        show_in_menu: data.show_in_menu === '1',
                        menu_position: Number(data.menu_position) || 25,
                        menu_icon: data.menu_icon || 'dashicons-admin-post',
                        show_in_rest: data.show_in_rest === '1',
                    },
                    rewrite_slug: data.rewrite_slug || '',
                });

                // Determine rewriteSlugType based on fetched data
                const generated = generateMetaKey(data.slug || '');
                if (data.rewrite_slug === 'prevent') {
                    setRewriteSlugType('prevent');
                } else if (data.rewrite_slug && data.rewrite_slug !== generated) {
                    setRewriteSlugType('custom');
                    setCustomRewriteSlug(data.rewrite_slug);
                } else {
                    setRewriteSlugType('default');
                }
            })
            .catch(error => {
                showNotice(error?.message || __('Error fetching taxonomy.', 'voxycure-framework'), 'error');
            });
    }, [params.id]);

    const handleSave = () => {
        setIsSaving(true);

        const finalRewriteSlug =
            rewriteSlugType === 'default'
                ? generateMetaKey(taxonomyData.slug || '')
                : rewriteSlugType === 'prevent'
                    ? 'prevent'
                    : customRewriteSlug;

        // Validate placeholders before saving
        if (rewriteSlugType === 'custom' && !validatePlaceholders(finalRewriteSlug)) {
            showNotice(__('Invalid placeholder used. Only {term} and {date} are allowed.', 'voxycure-framework'), 'error');
            setIsSaving(false);
            return;
        }

        saveTaxonomy({
            ...taxonomyData,
            rewrite_slug: finalRewriteSlug,
        })
            .then(savedData => {
                setTaxonomyData(prev => ({ ...prev, id: savedData?.data?.id }));
                updateUrlParams({ view: 'edit-taxonomy', id: savedData?.data?.id });
                setTimeout(() => {
                    showNotice(savedData?.message || __('Taxonomy saved successfully.', 'voxycure-framework'), 'success');
                }, 50);
            })
            .catch(error => {
                showNotice(error?.message || __('Error saving taxonomy.', 'voxycure-framework'), 'error');
            })
            .finally(() => setIsSaving(false));
    };

    const tabList = [
        { name: 'taxonomy-settings', title: __('Taxonomy Settings', 'voxycure-framework'), className: 'taxonomy-settings-tab' },
        // { name: 'labels', title: __('Labels', 'voxycure-framework'), className: 'labels-tab' },
        { name: 'visibility', title: __('Visibility', 'voxycure-framework'), className: 'visibility-tab' },
    ];

    return (
        <>
            <Header>
                <h2 className="voxycure-editor__header-title">
                    {taxonomyData.id ? __('Edit Taxonomy', 'voxycure-framework') : __('Add New Taxonomy', 'voxycure-framework')}
                </h2>
                <Button
                    className="components-button is-primary voxycure-button"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <Spinner style={{ verticalAlign: 'middle', marginRight: 6 }} size={16} />
                            {__('Saving...', 'voxycure-framework')}
                        </>
                    ) : __('Save', 'voxycure-framework')}
                </Button>
            </Header>

            <Body>
                <Tabs
                    tabs={tabList}
                    renderTab={(tab) => {
                        switch (tab.name) {
                            case 'taxonomy-settings':
                                return (
                                    <>
                                        <TextControl
                                            label={__('Plural Label *', 'voxycure-framework')}
                                            value={taxonomyData.name || ''}
                                            onChange={(value) => setTaxonomyData({ ...taxonomyData, name: value })}
                                            required
                                        />

                                        <TextControl
                                            label={__('Singular Label *', 'voxycure-framework')}
                                            value={taxonomyData.singular_name || ''}
                                            onChange={(value) => setTaxonomyData({ ...taxonomyData, singular_name: value })}
                                            required
                                        />

                                        <FormTokenField
                                            label={__('Select Post Types', 'voxycure-framework')}
                                            value={taxonomyData.post_types || []}
                                            suggestions={postTypeSuggestions}
                                            onChange={(value) => setTaxonomyData({ ...taxonomyData, post_types: value })}
                                        />

                                        <TextControl
                                            label={__('Taxonomy Slug *', 'voxycure-framework')}
                                            value={taxonomyData.slug || ''}
                                            onChange={(value) => {
                                                setTaxonomyData({ ...taxonomyData, slug: value });

                                                if (rewriteSlugType === 'default') {
                                                    setTaxonomyData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: generateMetaKey(value),
                                                    }));
                                                }
                                            }}
                                            required
                                        />

                                        <TextControl
                                            label={__('Menu Name', 'voxycure-framework')}
                                            value={taxonomyData.labels?.menu_name || ''}
                                            onChange={(value) =>
                                                setTaxonomyData({ ...taxonomyData, labels: { ...taxonomyData.labels, menu_name: value } })
                                            }
                                        />

                                        <SelectControl
                                            label={__('Rewrite Slug', 'voxycure-framework')}
                                            value={rewriteSlugType}
                                            options={[
                                                { label: __('Taxonomy key (default)', 'voxycure-framework'), value: 'default' },
                                                { label: __('No Permalink (prevent URL rewriting)', 'voxycure-framework'), value: 'prevent' },
                                                { label: __('Custom', 'voxycure-framework'), value: 'custom' },
                                            ]}
                                            onChange={(value) => {
                                                setRewriteSlugType(value);

                                                if (value === 'default') {
                                                    const autoSlug = generateMetaKey(taxonomyData.name || '');
                                                    setTaxonomyData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: autoSlug,
                                                    }));
                                                } else if (value === 'prevent') {
                                                    setTaxonomyData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: 'prevent',
                                                    }));
                                                }
                                            }}
                                        />

                                        {rewriteSlugType === 'custom' && (
                                            <TextControl
                                                label={__('Custom Rewrite Slug', 'voxycure-framework')}
                                                value={customRewriteSlug}
                                                onChange={(value) => {
                                                    const sanitized = generateMetaKey(value);
                                                    setCustomRewriteSlug(sanitized);
                                                    setTaxonomyData(prev => ({
                                                        ...prev,
                                                        rewrite_slug: sanitized,
                                                    }));
                                                }}
                                                help={__('You can use / and placeholders {term}, {date}.', 'voxycure-framework')}
                                            />
                                        )}
                                    </>
                                );

                            case 'labels':
                                return (
                                    <>
                                        {/* Placeholder for Labels tab content */}
                                    </>
                                );

                            case 'visibility':
                                return (
                                    <>
                                        <ToggleControl
                                            label={__('Show In UI', 'voxycure-framework')}
                                            checked={!!taxonomyData.visibility?.show_in_ui}
                                            onChange={(value) =>
                                                setTaxonomyData({ ...taxonomyData, visibility: { ...taxonomyData.visibility, show_in_ui: value } })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Show In Admin Menu', 'voxycure-framework')}
                                            checked={!!taxonomyData.visibility?.show_in_menu}
                                            onChange={(value) =>
                                                setTaxonomyData({ ...taxonomyData, visibility: { ...taxonomyData.visibility, show_in_menu: value } })
                                            }
                                        />
                                        <TextControl
                                            label={__('Menu Position', 'voxycure-framework')}
                                            value={taxonomyData.visibility?.menu_position || ''}
                                            onChange={(value) =>
                                                setTaxonomyData({ ...taxonomyData, visibility: { ...taxonomyData.visibility, menu_position: value } })
                                            }
                                        />
                                        <TextControl
                                            label={__('Menu Icon', 'voxycure-framework')}
                                            value={taxonomyData.visibility?.menu_icon || ''}
                                            onChange={(value) =>
                                                setTaxonomyData({ ...taxonomyData, visibility: { ...taxonomyData.visibility, menu_icon: value } })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Show In REST API', 'voxycure-framework')}
                                            checked={!!taxonomyData.visibility?.show_in_rest}
                                            onChange={(value) =>
                                                setTaxonomyData({ ...taxonomyData, visibility: { ...taxonomyData.visibility, show_in_rest: value } })
                                            }
                                        />
                                        <ToggleControl
                                            label={__('Public', 'voxycure-framework')}
                                            checked={!!taxonomyData.public}
                                            onChange={(value) => setTaxonomyData({ ...taxonomyData, public: value })}
                                        />
                                        <ToggleControl
                                            label={__('Hierarchical', 'voxycure-framework')}
                                            checked={!!taxonomyData.hierarchical}
                                            onChange={(value) => setTaxonomyData({ ...taxonomyData, hierarchical: value })}
                                        />
                                    </>
                                );

                            default:
                                return null;
                        }
                    }}
                />
            </Body>
        </>
    );
};

export default TaxonomyEdit;
